/**
 * @fileoverview Helpers for the Active Clients screen.
 * @description What the grid, the details and the dialogs share: whether a client's details list an
 * action, how the HR Admin state and the client status read as badges, a client's initials, and the
 * two conversions of the Edit Client form (a loaded client into form values, validated values back
 * into the PATCH body). The edit body is a full replacement, so everything the form does not show
 * (the primary contact's office phone, the stored country code) is sent back as loaded, and the
 * secondary contact is left out so it is kept.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { absoluteWebsite, INDIA_COUNTRY_CODE } from '@/features/sales/lib/prospect-form.utils'
import { ApiException } from '@/shared/api/api.exception'

import type { EditClientFormValues } from '@/features/sales/schemas/edit-client-form.schema'
import type {
  ClientAction,
  ClientDetails,
  ClientHrAdminState,
  EditClientRequest,
  SalesClientSortField,
  SalesClientStatus,
} from '@/features/sales/types/sales-client.types'

/** @description The grid's page size. */
export const CLIENT_PAGE_SIZE = 25

/** @description The filter value that means "no filter". */
export const ALL_CLIENTS_FILTER = ''

/** @description What the grid is sorted by and which way. */
export interface ClientSort {
  field: SalesClientSortField
  direction: 'asc' | 'desc'
}

/** @description The grid's sort when it opens: newest clients first. */
export const DEFAULT_CLIENT_SORT: ClientSort = { field: 'onboardedAt', direction: 'desc' }

/**
 * @description The sort after a click on a column: the same column reverses, a new one starts
 * ascending (the date column starts newest first).
 * @param {ClientSort} current - The sort now.
 * @param {SalesClientSortField} field - The column that was clicked.
 * @returns {ClientSort} The sort to apply.
 */
export function nextSort(current: ClientSort, field: SalesClientSortField): ClientSort {
  if (current.field === field) {
    return { field, direction: current.direction === 'asc' ? 'desc' : 'asc' }
  }
  return { field, direction: field === 'onboardedAt' ? 'desc' : 'asc' }
}

/** @description The badge colour of each HR Admin state. */
export const HR_ADMIN_BADGE_VARIANT: Record<ClientHrAdminState, 'neutral' | 'warning' | 'success'> =
  {
    NOT_INVITED: 'neutral',
    INVITED: 'warning',
    ACTIVE: 'success',
  }

/** @description The badge colour of each client status. */
export const CLIENT_STATUS_BADGE_VARIANT: Record<SalesClientStatus, 'success' | 'neutral'> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
}

/**
 * @description Whether an Edit failed because the client changed after it was read: only
 * `412 / 4105`. A `409` on a client is a different refusal (registration number in use, archived,
 * already invited), so unlike a prospect's it never means a stale version.
 * @param {unknown} error - The mutation's error.
 * @returns {boolean} True for a 412 response.
 */
export function isStaleClientVersion(error: unknown): boolean {
  return error instanceof ApiException && error.status === 412
}

/**
 * @description Whether the details list an action as allowed for the caller right now. The screen
 * never derives this from the caller's role.
 * @param {ClientDetails} client - The client's details.
 * @param {ClientAction} action - The action.
 * @returns {boolean} True when the backend allows it.
 */
export function hasClientAction(client: ClientDetails, action: ClientAction): boolean {
  return client.allowedActions.includes(action)
}

/**
 * @description A bank name's two-letter initials.
 * @param {string} name - The bank's name.
 * @returns {string} Up to two uppercase initials.
 */
export function clientInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * @description The date part of an ISO timestamp, or a dash when there is none.
 * @param {string | null | undefined} iso - The timestamp.
 * @returns {string} `YYYY-MM-DD`, or an em dash.
 */
export function dayOf(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—'
}

/**
 * @description The Edit form's starting values: the client as it is now. The note field starts empty,
 * because a note typed on Edit is appended to the client's notes rather than replacing them. Needs
 * the `sales` block, which only a Super Admin (the only role that may edit) is given.
 * @param {ClientDetails} client - The loaded client.
 * @returns {EditClientFormValues | null} The form's values, or null when the client has no `sales`
 * block to prefill the lead source and rating from.
 */
export function editValuesFromClient(client: ClientDetails): EditClientFormValues | null {
  if (!client.sales) return null
  const { organisation, location, primaryContact, sales } = client
  return {
    name: client.bankName,
    bankTypeId: organisation.bankType.id,
    headOffice: organisation.headOfficeLocation,
    regNumber: organisation.registrationNumber ?? '',
    website: organisation.website ?? '',
    stateId: location.state.id,
    city: location.city,
    district: location.district ?? '',
    pincode: location.pinCode ?? '',
    address: organisation.streetAddress ?? '',
    branches: organisation.branchCount,
    ...(organisation.employeeCount !== null && { employees: organisation.employeeCount }),
    coreBankingSolutionId: organisation.coreBankingSolution?.id ?? '',
    existingMis: organisation.existingMisSolution ?? '',
    contactName: primaryContact.name,
    contactDesignation: primaryContact.designation ?? '',
    contactEmail: primaryContact.email,
    contactPhone: primaryContact.mobileNumber ?? '',
    leadSourceId: sales.leadSource.id,
    leadRating: sales.leadRating,
    notes: '',
  }
}

/**
 * @description Whether the edit changes the registration number or the bank type, the two changes
 * that need a confirmation before they are sent.
 * @param {EditClientFormValues} values - The validated form values.
 * @param {ClientDetails} client - The client as it was loaded.
 * @returns {boolean} True when either changed.
 */
export function changesBankIdentity(values: EditClientFormValues, client: ClientDetails): boolean {
  return (
    values.regNumber !== (client.organisation.registrationNumber ?? '') ||
    values.bankTypeId !== client.organisation.bankType.id
  )
}

/**
 * @description Turns the validated Edit form into Edit Client's body. The stored country code and the
 * primary contact's office phone are sent back as loaded (the form shows neither), and the secondary
 * contact is left out, so it is kept.
 * @param {EditClientFormValues} values - The validated form values.
 * @param {ClientDetails} client - The client as it was loaded.
 * @returns {EditClientRequest} The PATCH body.
 */
export function toEditClientRequest(
  values: EditClientFormValues,
  client: ClientDetails,
): EditClientRequest {
  return {
    bank: {
      name: values.name,
      bankTypeId: values.bankTypeId,
      registrationNumber: values.regNumber || null,
      headOfficeLocation: values.headOffice,
      website: absoluteWebsite(values.website),
    },
    address: {
      streetAddress: values.address,
      stateId: values.stateId,
      city: values.city,
      district: values.district,
      pinCode: values.pincode,
    },
    organisation: {
      branchCount: values.branches,
      employeeCount: values.employees ?? null,
      coreBankingSolutionId: values.coreBankingSolutionId,
      existingMisSolution: values.existingMis || null,
    },
    primaryContact: {
      name: values.contactName,
      designation: values.contactDesignation || null,
      email: values.contactEmail,
      countryCode: client.primaryContact.countryCode ?? INDIA_COUNTRY_CODE,
      mobileNumber: values.contactPhone,
      officePhone: client.primaryContact.officePhone,
    },
    sales: {
      leadSourceId: values.leadSourceId,
      leadRating: values.leadRating,
      notes: values.notes || null,
    },
  }
}
